import { useId, useState } from 'react';
import { chartInk } from '../../design-system/tokens';
import './Distribution.css';

export interface DistributionDatum {
  label: string;
  count: number;
}

export interface DistributionProps {
  /** What is plotted — read by screen readers, and shown as the caption. */
  title: string;
  data: DistributionDatum[];
  /** Word for one unit, e.g. "rows". Used in the tooltip and the table view. */
  unit?: string;
  height?: number;
}

const MAX_BAR = 24; // never fill the band: the leftover is air
const GAP = 2; // a surface gap separates touching bars
const RADIUS = 4; // rounded data-end, square at the baseline

/**
 * A single-series column chart: histogram bins, or counts per value.
 *
 * One series means one colour and no legend — the caption says what is
 * plotted. Values are not labelled on every bar; the hover tooltip and the
 * table view carry them instead.
 */
export function Distribution({ title, data, unit = 'rows', height = 120 }: DistributionProps) {
  const [hovered, setHovered] = useState<number | null>(null);
  const tableId = useId();
  const [showTable, setShowTable] = useState(false);

  if (!data.length) {
    return <p className="sc-chart__empty">Nothing to plot for this column.</p>;
  }

  const width = 600;
  const max = Math.max(...data.map((item) => item.count), 1);
  const band = width / data.length;
  const barWidth = Math.max(1, Math.min(MAX_BAR, band - GAP));
  const plotHeight = height - 1; // leave room for the baseline rule

  return (
    <figure className="sc-chart">
      <svg
        className="sc-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${title}. ${data.length} groups. Highest ${max.toLocaleString()} ${unit}.`}
        onMouseLeave={() => setHovered(null)}
      >
        {/* Baseline: hairline, one step off the surface, recessive. */}
        <line
          x1="0"
          y1={plotHeight}
          x2={width}
          y2={plotHeight}
          stroke={chartInk.axis}
          strokeWidth="1"
        />

        {data.map((item, index) => {
          const barHeight = Math.max(1, (item.count / max) * (plotHeight - 4));
          const x = index * band + (band - barWidth) / 2;
          const y = plotHeight - barHeight;
          const radius = Math.min(RADIUS, barHeight, barWidth / 2);

          return (
            <g key={`${item.label}-${index}`}>
              <path
                d={`M ${x} ${plotHeight}
                    L ${x} ${y + radius}
                    Q ${x} ${y} ${x + radius} ${y}
                    L ${x + barWidth - radius} ${y}
                    Q ${x + barWidth} ${y} ${x + barWidth} ${y + radius}
                    L ${x + barWidth} ${plotHeight} Z`}
                fill={chartInk.series}
                opacity={hovered === null || hovered === index ? 1 : 0.55}
              />
              {/* A hit target wider than the mark, so hovering is not fiddly. */}
              <rect
                x={index * band}
                y={0}
                width={band}
                height={height}
                fill="transparent"
                onMouseEnter={() => setHovered(index)}
              />
            </g>
          );
        })}
      </svg>

      <figcaption className="sc-chart__caption">
        {hovered !== null && data[hovered] ? (
          <span className="sc-chart__tooltip" role="status">
            <strong>{data[hovered].label}</strong> · {data[hovered].count.toLocaleString()} {unit}
          </span>
        ) : (
          <span className="sc-chart__range">
            {data[0]?.label} … {data[data.length - 1]?.label}
          </span>
        )}
        <button
          type="button"
          className="sc-chart__toggle"
          aria-expanded={showTable}
          aria-controls={tableId}
          onClick={() => setShowTable((open) => !open)}
        >
          {showTable ? 'Hide values' : 'Show values'}
        </button>
      </figcaption>

      {/* The table view: every value is reachable without hovering anything. */}
      <table id={tableId} className="sc-chart__table" hidden={!showTable}>
        <caption className="sc-visually-hidden">{title}</caption>
        <thead>
          <tr>
            <th scope="col">Group</th>
            <th scope="col">{unit[0]?.toUpperCase() + unit.slice(1)}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={`${item.label}-row-${index}`}>
              <td>{item.label}</td>
              <td className="sc-chart__num">{item.count.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
